import React, { useEffect, useMemo, useState } from "react";
import { PALETTES } from "./palettes";
import { faceForMood, layersFor, resolveAvatar } from "./identity";
import { peek, recolor } from "./recolor";

import type { AvatarLook } from "@/shared/types";
import type { SpriteLayer } from "./identity";

/* ----------------------------------------------------------------------------
   The head-shot renderer.

   Sheets are 128×256 — a 2-frame animation across 4 direction rows of 64×64.
   We want one cell: south-facing, frame 0. Within that cell the head occupies
   a measured box; CROP is that box squared off and padded upward so tall hair
   and future hats aren't sliced.

   Only the head is drawn. At the sizes this app uses — 13 px in a task row,
   22 px in a chat line — a full body is a smudge, and the face is the whole
   point: it carries the mood.
---------------------------------------------------------------------------- */

const SHEET_W = 128;
const SHEET_H = 256;
const CELL = 64;
const ROWS = { n: 0, w: CELL, s: CELL * 2, e: CELL * 3 };
const CROP = { x: 19, y: 11, size: 26 };

/* The idle sheet is two hand-drawn frames, not one. Alternating them is the
   whole idle animation — the artist already solved what a head does when it is
   doing nothing, and a drawn frame can't land off the pixel grid the way a CSS
   translate can. Slow, uneven cadence so a row of avatars doesn't march. */
const IDLE_MS = 620;

const stillness = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function useIdleFrame(enabled: boolean) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    if (!enabled || stillness()) return;
    const t = setInterval(() => setFrame((f) => (f ? 0 : 1)), IDLE_MS + Math.random() * 220);
    return () => clearInterval(t);
  }, [enabled]);
  return enabled ? frame : 0;
}

/* Blink is what separates a character from a sticker. Cheap: swap the
   expression sheet for the closed-eyes one for a moment. */
const BLINK_EVERY = 4200;
const BLINK_MS = 130;

function useBlink(enabled: boolean) {
  const [blinking, setBlinking] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let shut: ReturnType<typeof setTimeout>;
    // Stagger by a random offset so a sidebar of agents doesn't blink in unison.
    const open = setInterval(() => {
      setBlinking(true);
      shut = setTimeout(() => setBlinking(false), BLINK_MS);
    }, BLINK_EVERY + Math.random() * 2600);
    return () => {
      clearInterval(open);
      clearTimeout(shut);
    };
  }, [enabled]);
  return blinking;
}

function useLayers(layers: SpriteLayer[]) {
  const [srcs, setSrcs] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(layers.map((l) => [l.key, peek(l.src, l.material, l.ramp)]))
  );
  useEffect(() => {
    let live = true;
    Promise.all(
      layers.map((l) => recolor(l.src, l.material, l.ramp).then((url) => [l.key, url]))
    ).then((pairs) => live && setSrcs(Object.fromEntries(pairs)));
    return () => {
      live = false;
    };
  }, [layers]);
  return srcs;
}

/* `animate` and `emotion` are `string` rather than unions: the values arrive
   from prop-drilled world state ("alive", "run", every AgentMood), and both
   are read through lookups that already
   fall back. Narrowing them here would only move the lie. */
export interface LpcAvatarProps {
  seed?: string;
  size?: number;
  rounded?: number;
  ring?: boolean;
  animate?: string | null;
  emotion?: string | null;
  charged?: boolean;
  human?: boolean;
  facing?: string;
  blink?: boolean;
  avatar?: Partial<AvatarLook> | null;
  className?: string;
}

export default function LpcAvatar({
  seed = "agent",
  size = 24,
  rounded = 4,
  ring = false,
  animate = null,
  emotion = null,
  charged = false,
  human = false,
  facing = "s",
  blink = true,
  avatar: override = null,
  className = "",
}: LpcAvatarProps) {
  const avatar = useMemo(
    () => resolveAvatar(seed, { human, override }),
    [seed, human, override]
  );

  const blinking = useBlink(!!animate && blink && emotion !== "sleeping");
  const frame = useIdleFrame(!!animate && emotion !== "sleeping");
  const row = ROWS[facing as keyof typeof ROWS] ?? ROWS.s;
  const face = blinking ? "closed" : faceForMood(emotion);
  const layers = useMemo(() => layersFor(avatar, { face }), [avatar, face]);
  const srcs = useLayers(layers);

  const scale = size / CROP.size;
  const ramp = PALETTES.skin[avatar.skin] || PALETTES.skin.$base;

  /* The frame shape is the second agent/human tell, and it has to be
     unambiguous — a person is a circle, an agent is a squircle, whatever the
     caller asked for. It reads at 13 px where six pixels of skin hue don't. */
  const radius = human ? size / 2 : Math.min(rounded, size * 0.3);

  /* Only the states the sheets can't draw get a CSS transform: sleeping (a
     long slow breath) and run (a hop). Plain "alive" is carried by the sheet's
     own two frames — `charged` never meant motion, only "no container, halo". */
  const motion = emotion === "sleeping" ? "lpc-sleep" : animate === "run" ? "lpc-run" : "";

  const sprite = (
    <div
      className={`relative ${motion}`}
      /* One art pixel, rounded to a whole screen pixel: idle motion that lands
         between pixels smears a pixel-art head into mush. */
      style={{ width: size, height: size, "--lpc-lift": `${Math.max(1, Math.round(scale))}px` } as React.CSSProperties}
    >
      {layers.map((l) =>
        srcs[l.key] ? (
          <div
            key={l.key}
            className="absolute inset-0 pixelated"
            style={{
              backgroundImage: `url(${srcs[l.key]})`,
              backgroundSize: `${SHEET_W * scale}px ${SHEET_H * scale}px`,
              backgroundPosition: `${-(CROP.x + frame * CELL) * scale}px ${-(row + CROP.y) * scale}px`,
              backgroundRepeat: "no-repeat",
              imageRendering: "pixelated",
              // A tight, dim halo. The old 12x12 sprite could take a heavy glow
              // because it was mostly flat colour; a shaded head just washes out.
              filter: charged ? `drop-shadow(0 0 ${size * 0.1}px ${ramp[3]}99)` : undefined,
            }}
          />
        ) : null
      )}
    </div>
  );

  if (charged) {
    return (
      <span
        className={className}
        style={{ display: "inline-block", width: size, height: size, flexShrink: 0 }}
      >
        {sprite}
      </span>
    );
  }

  return (
    <div
      className={`inline-block overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: `linear-gradient(180deg, ${ramp[4]}33, ${ramp[2]}44), var(--surface-2)`,
        boxShadow: ring ? "0 0 0 1.5px var(--hairline-strong)" : undefined,
        flexShrink: 0,
      }}
    >
      {sprite}
    </div>
  );
}
